import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface DropoutData {
  current_dropout_rate: number;
  trend: 'increasing' | 'decreasing' | 'stable' | 'insufficient_data';
  dropout_by_level: {
    primary: number;
    secondary: number;
  };
  dropout_by_gender: {
    male: number;
    female: number;
  };
  high_risk_groups: Array<{
    group: string;
    count: number;
    risk_level: string;
    description: string;
  }>;
  predictions: {
    next_year_rate: number;
    confidence: string;
    factors: string[];
  };
  recommendations: Array<{
    priority: string;
    action: string;
    description: string;
  }>;
}

const DropoutAnalytics: React.FC = () => {
  const { data, isLoading, error } = useQuery<{ data: DropoutData }>({
    queryKey: ['dropout-analytics'],
    queryFn: async () => {
      const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
      const response = await axios.get(`${API_URL}/core/dropout-analytics/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error || !data) {
    console.error('Dropout analytics error:', error);
    return (
      <Alert severity="error">
        Failed to load dropout analytics
      </Alert>
    );
  }

  const analytics = data.data;

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon color="error" />;
      case 'decreasing':
        return <TrendingDownIcon color="success" />;
      default:
        return <TrendingUpIcon color="disabled" />;
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Box>
      {/* Current Status */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Current Dropout Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="h3" component="span">
                  {analytics.current_dropout_rate}%
                </Typography>
                {getTrendIcon(analytics.trend)}
              </Box>
              <Chip
                label={analytics.trend}
                size="small"
                color={analytics.trend === 'decreasing' ? 'success' : 'error'}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Predicted Next Year
              </Typography>
              <Typography variant="h3" component="span">
                {analytics.predictions.next_year_rate}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Confidence: {analytics.predictions.confidence}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                By Level
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Primary</Typography>
                  <Typography variant="body2">{analytics.dropout_by_level.primary}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={analytics.dropout_by_level.primary}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Secondary</Typography>
                  <Typography variant="body2">{analytics.dropout_by_level.secondary}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={analytics.dropout_by_level.secondary}
                  color="secondary"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* High Risk Groups */}
      {analytics.high_risk_groups.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              High Risk Groups
            </Typography>
            <List>
              {analytics.high_risk_groups.map((group, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <WarningIcon color={getRiskColor(group.risk_level) as any} />
                  </ListItemIcon>
                  <ListItemText
                    primary={group.group}
                    secondary={`${group.count.toLocaleString()} students - ${group.description}`}
                  />
                  <Chip
                    label={group.risk_level}
                    size="small"
                    color={getRiskColor(group.risk_level) as any}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Recommendations
          </Typography>
          <List>
            {analytics.recommendations.map((rec, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {rec.priority === 'high' ? (
                    <WarningIcon color="error" />
                  ) : (
                    <CheckCircleIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={rec.action}
                  secondary={rec.description}
                />
                <Chip
                  label={rec.priority}
                  size="small"
                  color={rec.priority === 'high' ? 'error' : 'default'}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DropoutAnalytics;